import CalendarEvent from 'owa-calendar-types/lib/types/CalendarEvent';
import { AgendaItem } from '../store/schema/AgendaItem';
import { DateRange } from 'owa-datetime-utils';
import { isEventAMeeting } from 'owa-calendar-event-capabilities';
import { getShowDeclinedMeetingsEnabled } from './userConfigSelectors';
import {
    getLockedCalendarEventsReader,
    getLockedCalendarEventsStore,
    EventsCacheLockId,
    LockedCalendarEventsReader,
} from 'owa-calendar-events-store';
import { compareCalendarEvents } from 'owa-calendar-types';

interface AgendaLockedCalendarEventsReader extends LockedCalendarEventsReader {
    getOrderedEvents: (folderId: string[], dateRange: DateRange) => CalendarEvent[];
    getCalendarAgendaItems: (folderId: string[], dateRange: DateRange) => AgendaItem[];
}

export function getAgendaLockedCalendarEventsReader(
    lockId: EventsCacheLockId
): AgendaLockedCalendarEventsReader {
    const lockedReader = getLockedCalendarEventsReader(lockId);
    return {
        ...lockedReader,
        getOrderedEvents: (folderId: string[], dateRange: DateRange) =>
            getOrderedEvents(folderId, dateRange, lockId),
        getCalendarAgendaItems: (folderId: string[], dateRange: DateRange) =>
            getOrderedEvents(folderId, dateRange, lockId).map(getAgendaItem),
    };
}

export { getLockedCalendarEventsStore };

function getOrderedEvents(
    folderId: string[],
    dateRange: DateRange,
    lockId: EventsCacheLockId
): CalendarEvent[] {
    const getCalendarEventsForDateRange = getLockedCalendarEventsReader(lockId)
        .getCalendarEventsForDateRange;
    let events: CalendarEvent[] = [];
    folderId.forEach(folder => {
        events.push(...getCalendarEventsForDateRange(folder, dateRange));
    });

    if (!getShowDeclinedMeetingsEnabled()) {
        events = events.filter(event => event.ResponseType !== 'Decline');
    }

    return events.sort(compareCalendarEvents);
}

function getAgendaItem(calendarEvent: CalendarEvent): AgendaItem {
    return {
        id: calendarEvent.ItemId,
        subject: calendarEvent.Subject,
        startTime: calendarEvent.Start,
        endTime: calendarEvent.End,
        location: calendarEvent.Location && calendarEvent.Location.DisplayName,
        organizerName: calendarEvent.Organizer && calendarEvent.Organizer.Mailbox.Name,
        organizerEmail: calendarEvent.Organizer && calendarEvent.Organizer.Mailbox.EmailAddress,
        isAllDay: calendarEvent.IsAllDayEvent,
        isCancelled: calendarEvent.IsCancelled || calendarEvent.ResponseType === 'Decline',
        freeBusyType: calendarEvent.FreeBusyType,
        parentFolderId: calendarEvent.ParentFolderId,
        categories: calendarEvent.Categories,
        isRecurring: calendarEvent.CalendarItemType === 'Occurrence',
        hasAttachments: calendarEvent.HasAttachments,
        isRecurringException: calendarEvent.CalendarItemType === 'Exception',
        isPrivate: calendarEvent.Sensitivity === 'Private',
        isMeeting: isEventAMeeting(calendarEvent),
        joinUrl: calendarEvent.OnlineMeetingJoinUrl,
        provider: calendarEvent.OnlineMeetingProvider,
    };
}
